// Cookie Spring writes when /api/auth/csrf is called.
// Example cookie string in browser storage:
// "XSRF-TOKEN=abc123%3D%3D; JSESSIONID=DEF456"
const CSRF_COOKIE_NAME = "XSRF-TOKEN";
// Header Spring Security expects on mutating requests.
// Example outgoing header: { "X-XSRF-TOKEN": "abc123==" }
const CSRF_HEADER_NAME = "X-XSRF-TOKEN";
const CSRF_STORAGE_KEY = "myteengig.csrf-token";

let inMemoryCsrfToken: string | null = null;

// Reads one cookie value from document.cookie by key name.
// Input example: name = "XSRF-TOKEN"
// Output example when found: "abc123=="
// Will output null when cookie value is missing.
function getCookieValue(name: string): string | null {
  // document.cookie is a single string like: XSRF-TOKEN=abc123%3D%3D; JSESSIONID=DEF456"
  // Split by "; " so each item becomes "key=value". ["XSRF-TOKEN=abc123%3D%3D", "JSESSIONID=DEF456"]
  // document.cookie is the browser's cookie string for current page origin.
  const cookiePairs = document.cookie ? document.cookie.split("; ") : [];

  // Walk every "key=value" cookie pair.
  for (const pair of cookiePairs) {
    // Split once on "=":
    // "XSRF-TOKEN=abc123%3D%3D" -> cookieKey: "XSRF-TOKEN", rest: ["abc123%3D%3D"]
    // Join the rest back in case value itself contains "=" characters.
    const [cookieKey, ...rest] = pair.split("=");

    // Skip cookies that are not the requested name.
    if (cookieKey !== name) {
      continue;
    }

    // URL-decode cookie value before returning. (Not every cookie may be encoded but this is standard.)
    // Example: "abc123%3D%3D" -> "abc123=="
    return decodeURIComponent(rest.join("="));
  }

  // Cookie key was not present.
  return null;
}

function getStoredCsrfToken(): string | null {
  if (inMemoryCsrfToken) {
    return inMemoryCsrfToken;
  }

  if (typeof window === "undefined") {
    return null;
  }

  const stored = window.sessionStorage.getItem(CSRF_STORAGE_KEY);
  if (stored) {
    inMemoryCsrfToken = stored;
  }
  return stored;
}

export function setCsrfToken(token: string): void {
  inMemoryCsrfToken = token;

  if (typeof window !== "undefined") {
    window.sessionStorage.setItem(CSRF_STORAGE_KEY, token);
  }
}

// Builds the CSRF header map used by the shared API client.
// Return example with token: { "X-XSRF-TOKEN": "abc123==" }
// Return example without token: {}
export function buildCsrfHeader(): HeadersInit {
  const token = getStoredCsrfToken() ?? getCookieValue(CSRF_COOKIE_NAME);
  // If token exists, map it to Spring's expected header name.
  // If token does not exist, return an empty header object.
  return token ? { [CSRF_HEADER_NAME]: token } : {};
}

export function hasCsrfToken(): boolean {
  return getStoredCsrfToken() !== null || getCookieValue(CSRF_COOKIE_NAME) !== null;
}
